'use client';

import { useEffect, useCallback, useRef } from 'react';
import { create } from 'zustand';

export interface GeolocationState {
    accuracy: number | null;
    altitude: number | null;
    altitudeAccuracy: number | null;
    heading: number | null;
    latitude: number | null;
    longitude: number | null;
    speed: number | null;
    timestamp: number | null;
    loading: boolean;
    error: GeolocationPositionError | null;
}

const defaultState: GeolocationState = {
    accuracy: null,
    altitude: null,
    altitudeAccuracy: null,
    heading: null,
    latitude: null,
    longitude: null,
    speed: null,
    timestamp: null,
    loading: false,
    error: null,
};

export interface GeolocationOptions {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
}

const defaultOptions: GeolocationOptions = {
    enableHighAccuracy: false,
    timeout: Infinity,
    maximumAge: 0,
};

const useGeolocationStore = create<GeolocationState>()(() => defaultState);

// Hook from: https://www.usehooks.io/docs/use-geolocation
export function useGeolocation(options: GeolocationOptions = defaultOptions) {
    const state = useGeolocationStore();

    const watchIdRef = useRef<number | null>(null);
    const optionsRef = useRef(options);

    const onSuccess = useCallback((position: GeolocationPosition) => {
        useGeolocationStore.setState({
            loading: false,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            altitudeAccuracy: position.coords.altitudeAccuracy,
            heading: position.coords.heading,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            speed: position.coords.speed,
            timestamp: position.timestamp,
            error: null,
        });
    }, []);

    const onError = useCallback((error: GeolocationPositionError) => {
        useGeolocationStore.setState((prev) => ({
            ...prev,
            loading: false,
            error,
        }));
    }, []);

    const getCurrentPosition = useCallback(
        (): Promise<GeolocationPosition | null> =>
            new Promise((resolve) => {
                if (!navigator.geolocation) {
                    onError({
                        code: GeolocationPositionError.POSITION_UNAVAILABLE,
                        message: 'Geolocation is not supported by this browser.',
                    } as GeolocationPositionError);
                    resolve(null);
                    return;
                }

                useGeolocationStore.setState((prev) => ({ ...prev, loading: true, error: null }));
                navigator.geolocation.getCurrentPosition(
                    (data) => {
                        onSuccess(data);
                        resolve(data);
                    },
                    (error) => {
                        onError(error);
                        resolve(null);
                    },
                    optionsRef.current,
                );
            }),
        [onSuccess, onError],
    );

    const startWatching = useCallback(() => {
        if (!navigator.geolocation) {
            onError({
                code: GeolocationPositionError.POSITION_UNAVAILABLE,
                message: 'Geolocation is not supported by this browser.',
            } as GeolocationPositionError);
            return;
        }

        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
        }

        useGeolocationStore.setState((prev) => ({ ...prev, loading: true, error: null }));
        watchIdRef.current = navigator.geolocation.watchPosition(onSuccess, onError, optionsRef.current);
    }, [onSuccess, onError]);

    const stopWatching = useCallback(() => {
        if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
    }, []);

    // Update options ref when options change
    useEffect(() => {
        optionsRef.current = options;
    }, [options]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (watchIdRef.current !== null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
            }
        };
    }, []);

    return {
        ...state,
        getCurrentPosition,
        startWatching,
        stopWatching,
    };
}
